import { BackgroundCheckStatus } from '@/backend/features/background-check/types';
import { getAllBackgroundCheckStatuses } from '@/ui/features/background-check/utils';
import { FilterConfigBuilder } from '@juvare/common-data-table-ui/FiltersBar/utils';
import { WidgetFormPanelConfig } from '@juvare/common-form-ui/WidgetFormPanel';
import { AccountFilterData } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountList';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { hospitalEntityName } from '@juvare/credentialing/backend/config/classifiers/entities/defineHospitalClassifier';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { usePublicDataGet } from '@juvare/platform-sdk-react/ui/hooks';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export interface ExtendRmsFiltersParams {
    addBackgroundCheckStatusQuickFilter?: boolean;
    addBackgroundCheckTypeQuickFilter?: boolean;
}

export function useExtendedRmsAccountFilters() {
    const { t } = useTranslation('rms');
    const { backgroundCheckConfig, esarVhp } = useSelector(getSettings);
    const statuses: BackgroundCheckStatus[] = getAllBackgroundCheckStatuses(backgroundCheckConfig.type);
    const userState = useUserState();
    const statesAndTerritories = usePublicDataGet<{ value: string; label: string }[]>(`classifier-stateAndTerritory`);
    const currentStateOrTerritory = statesAndTerritories.data?.find(({ value }) => value === esarVhp.clientCode)?.label;

    const featureFlags = userState?.credentialing?.featureFlags ?? [];
    const extendRmsFilters = (builder: FilterConfigBuilder<AccountFilterData>, params: ExtendRmsFiltersParams = {}) => {
        if (featureFlags.includes('occupations')) {
            const occupationsAllFilterWidget = builder.findAllFiltersWidget('!occupation') as WidgetFormPanelConfig;
            occupationsAllFilterWidget.children.push(
                builder.allFiltersWidget({
                    key: 'hospitalNameIds',
                    label: t('accountsFilters.credentials.hospital'),
                    type: 'form.multiSelect',
                    valueProvider: {
                        type: `classifier.${hospitalEntityName}`,
                    },
                }),
                builder.allFiltersWidget({
                    key: 'issuingStatesOrJurisdictions',
                    label: t('accountsFilters.credentials.licenseStateJurisdiction'),
                    type: 'form.multiSelect',
                    disabled: {
                        whenNoValues: true,
                    },
                    valueProvider: {
                        type: 'stateAndTerritoryOrEmptyArray',
                        paramsFrom: '^allIssuingStatesOrJurisdictionsExceptCurrentClientCode',
                    },
                }),
                builder.allFiltersWidget({
                    key: 'allIssuingStatesOrJurisdictionsExceptCurrentClientCode',
                    label: t('accountsFilters.credentials.allExceptStateOrTerritory', {
                        stateOrTerritory: currentStateOrTerritory ?? esarVhp.clientCode,
                    }),
                    type: 'form.checkbox',
                    style: {
                        type: 'switch',
                    },
                }),
            );
        }

        if (featureFlags.includes('tribe')) {
            const locationPanel = builder.findAllFiltersWidget('!location') as WidgetFormPanelConfig;
            locationPanel.children.push(
                builder.allFiltersWidget({
                    label: t('accountsFilters.location.stateOfWorksLabel'),
                    type: 'form.select',
                    key: 'stateOfWork',
                    valueProvider: {
                        type: 'classifier.state',
                    },
                }),
            );
        }

        const backgroundCheckStatusOptions = statuses.map((value) => ({
            value: value,
            label: t(`backgroundCheck.statuses.${value}`),
        }));
        const backgroundCheckStatusWidget = params.addBackgroundCheckStatusQuickFilter
            ? builder.quickFilterMultiSelect({
                  dataPath: 'backgroundCheckStatus',
                  label: t('accountsFilters.backgroundCheck.status'),
                  widget: 'form.multiSelect',
                  values: backgroundCheckStatusOptions,
                  sx: { maxWidth: { xs: 1, sm: 280 } },
              })
            : builder.allFiltersWidget({
                  type: 'form.multiSelect',
                  label: t('accountsFilters.backgroundCheck.status'),
                  key: 'backgroundCheckStatus',
                  values: backgroundCheckStatusOptions,
              });

        const backgroundCheckTypeWidget = params.addBackgroundCheckTypeQuickFilter
            ? builder.quickFilterSelect({
                  widget: 'form.select',
                  dataPath: 'backgroundCheckType',
                  label: t('accountsFilters.backgroundCheck.type'),
                  valueProvider: {
                      type: 'backgroundCheckEnabledTypes',
                  },
                  sx: { maxWidth: { xs: 1, sm: 280 } },
              })
            : builder.allFiltersWidget({
                  type: 'form.select',
                  label: t('accountsFilters.backgroundCheck.type'),
                  key: 'backgroundCheckType',
                  valueProvider: {
                      type: 'backgroundCheckEnabledTypes',
                  },
              });

        builder.allFiltersPanel({
            key: '!backgroundCheck',
            label: t('accountsFilters.backgroundCheck.title'),
            children: [backgroundCheckTypeWidget, backgroundCheckStatusWidget],
        });
        builder.allFiltersPanel({
            key: '!esarVhp',
            label: t('accountsFilters.esarVhp.title'),
            children: [
                builder.allFiltersWidget({
                    key: 'esarVhpId',
                    label: '',
                    type: 'form.text',
                }),
            ],
        });
    };
    return { extendRmsFilters };
}
